import { useState, useMemo } from 'react';
import { Plus, Ban } from 'lucide-react';
import { useReactTable, getCoreRowModel, type ColumnDef } from '@tanstack/react-table';
import { toast } from '@/stores/toast-store';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { DataTable } from '@/components/data-table/data-table';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import {
  useRewardPrograms,
  useDeactivateRewardProgram,
  type RewardProgram,
} from '@/hooks/customer-engagement/use-reward-programs';
import { formatCurrency } from '@/lib/utils';
import { apiErrorMessage } from '@/lib/api-error';
import { usePermissions } from '@/hooks/use-permissions';
import { RewardProgramCreateDialog } from './reward-program-create-dialog';

type ActiveFilter = 'all' | 'active' | 'inactive';

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

const TYPE_LABEL: Record<RewardProgram['programType'], string> = {
  points_per_dollar: 'Per $ spent',
  points_per_visit: 'Per visit',
  points_per_product: 'Per product',
};

export function RewardProgramListSection() {
  const [filter, setFilter] = useState<ActiveFilter>('active');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [createOpen, setCreateOpen] = useState(false);
  const [toDeactivate, setToDeactivate] = useState<RewardProgram | null>(null);

  const { data, isLoading, isError, error } = useRewardPrograms(filter === 'all' ? undefined : filter === 'active');
  const deactivate = useDeactivateRewardProgram();
  const permissions = usePermissions();
  const canCreate = permissions.has('rewards.create');
  const canDeactivate = permissions.has('rewards.deactivate');

  const programs = data?.data ?? [];
  const total = programs.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pageRows = useMemo(() => programs.slice((page - 1) * perPage, page * perPage), [programs, page, perPage]);

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const columns: ColumnDef<RewardProgram>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => <span className="font-medium text-foreground">{row.original.name}</span>,
    },
    {
      accessorKey: 'programType',
      header: 'Type',
      cell: ({ row }) => <span className="whitespace-nowrap">{TYPE_LABEL[row.original.programType]}</span>,
    },
    {
      id: 'earnRate',
      header: () => <div className="text-right">Earn rate</div>,
      cell: ({ row }) => {
        const p = row.original;
        const earnRate =
          p.programType === 'points_per_dollar'
            ? `${Number(p.pointsPerDollar)} pts/$`
            : p.programType === 'points_per_visit'
              ? `${p.pointsPerVisit} pts/visit`
              : '—';
        return <div className="text-right whitespace-nowrap">{earnRate}</div>;
      },
    },
    {
      accessorKey: 'minOrderAmount',
      header: () => <div className="text-right">Min order</div>,
      cell: ({ row }) => (
        <div className="text-right whitespace-nowrap">
          {Number(row.original.minOrderAmount) > 0 ? formatCurrency(Number(row.original.minOrderAmount)) : '—'}
        </div>
      ),
    },
    {
      accessorKey: 'redemptionRate',
      header: () => <div className="text-right">Redeem rate</div>,
      cell: ({ row }) => (
        <div className="text-right whitespace-nowrap">
          {Number(row.original.redemptionRate) > 0 ? `$${Number(row.original.redemptionRate).toFixed(2)} / pt` : '—'}
        </div>
      ),
    },
    {
      accessorKey: 'cooldownMinutes',
      header: () => <div className="text-right">Cooldown</div>,
      cell: ({ row }) => (
        <div className="text-right">
          {row.original.cooldownMinutes === 0 ? (
            <span className="text-muted-foreground">none</span>
          ) : (
            `${row.original.cooldownMinutes} min`
          )}
        </div>
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => (
        <span
          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
            row.original.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
          }`}
        >
          {row.original.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          {row.original.isActive && canDeactivate && (
            <button
              type="button"
              onClick={() => setToDeactivate(row.original)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-orange-50"
              title="Deactivate"
              aria-label="Deactivate reward program"
            >
              <Ban className="h-4 w-4 text-orange-500" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: pageRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Reward Programs</h2>
          <p className="text-sm text-gray-600 mt-1">
            Configure loyalty rewards. Cooldown defaults to 0 (no restriction); set a positive value to throttle
            same-day repeat earn/redeem.
          </p>
        </div>
        {canCreate && (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" /> Create
          </button>
        )}
      </div>

      <div className="flex rounded-lg border border-border w-fit">
        {(['active', 'inactive', 'all'] as ActiveFilter[]).map((f, i) => (
          <button
            key={f}
            type="button"
            onClick={() => {
              setFilter(f);
              setPage(1);
            }}
            className={`px-3 py-2 text-sm font-medium ${
              filter === f ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
            } ${i === 0 ? 'rounded-l-lg' : ''} ${i === 2 ? 'rounded-r-lg' : ''}`}
          >
            {f === 'all' ? 'All' : f === 'active' ? 'Active' : 'Inactive'}
          </button>
        ))}
      </div>

      {isError && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          Failed to load programs: {apiErrorMessage(error)}
        </div>
      )}

      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">Loading...</div>
      ) : (
        !isError && (
          <>
            <DataTable table={table} columns={columns} />
            <DataTablePagination
              page={page}
              totalPages={totalPages}
              total={total}
              perPage={perPage}
              onPageChange={setPage}
              perPageOptions={PER_PAGE_OPTIONS}
              onPerPageChange={handlePerPageChange}
            />
          </>
        )
      )}

      <RewardProgramCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} />

      <ConfirmDialog
        open={!!toDeactivate}
        title="Deactivate reward program"
        message={
          toDeactivate
            ? `Deactivate "${toDeactivate.name}"? Cashiers won't be able to earn/redeem points against it. Existing point balances are preserved.`
            : ''
        }
        confirmLabel={deactivate.isPending ? 'Deactivating…' : 'Deactivate'}
        variant="warning"
        loading={deactivate.isPending}
        onConfirm={async () => {
          if (!toDeactivate) return;
          try {
            await deactivate.mutateAsync(toDeactivate.id);
            toast.success(`Program ${toDeactivate.name} deactivated`);
            setToDeactivate(null);
          } catch (e: any) {
            toast.error(e?.error?.message ?? 'Failed to deactivate');
          }
        }}
        onCancel={() => setToDeactivate(null)}
      />
    </div>
  );
}
